import { useEffect, useRef } from 'react';
import {
  queuePostView,
  setPostViewTrackerViewer,
} from '../services/postViewTracker';

const REQUIRED_VISIBILITY_RATIO = 0.5;
const REQUIRED_VISIBLE_TIME_MS = 2000;

const normalizeValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const normalizeUsername = (value) => {
  return normalizeValue(value).toLowerCase();
};

const usePostView = ({
  postId,
  postAuthorUsername,
  currentViewerUsername,
  isSponsored = false,
  enabled = true,
}) => {
  const postRef = useRef(null);
  const timerRef = useRef(null);
  const qualifiedRef = useRef(false);
  const isHalfVisibleRef = useRef(false);

  useEffect(() => {
    const normalizedPostId = normalizeValue(postId);
    const normalizedAuthor = normalizeUsername(postAuthorUsername);
    const normalizedViewer = normalizeUsername(currentViewerUsername);

    qualifiedRef.current = false;
    isHalfVisibleRef.current = false;

    const clearViewTimer = () => {
      if (!timerRef.current) return;

      clearTimeout(timerRef.current);
      timerRef.current = null;
    };

    if (
      !enabled ||
      isSponsored ||
      !normalizedPostId ||
      !normalizedViewer ||
      normalizedAuthor === normalizedViewer
    ) {
      clearViewTimer();
      return undefined;
    }

    const postElement = postRef.current;

    if (!postElement || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    setPostViewTrackerViewer(normalizedViewer);

    let observer;

    const qualifyView = () => {
      if (
        qualifiedRef.current ||
        !isHalfVisibleRef.current ||
        document.visibilityState !== 'visible'
      ) {
        return;
      }

      qualifiedRef.current = true;
      clearViewTimer();

      queuePostView(normalizedPostId, normalizedViewer);

      if (observer) {
        observer.disconnect();
      }
    };

    const startViewTimer = () => {
      if (
        qualifiedRef.current ||
        timerRef.current ||
        !isHalfVisibleRef.current ||
        document.visibilityState !== 'visible'
      ) {
        return;
      }

      timerRef.current = setTimeout(
        qualifyView,
        REQUIRED_VISIBLE_TIME_MS
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        clearViewTimer();
        return;
      }

      if (isHalfVisibleRef.current) {
        startViewTimer();
      }
    };

    observer = new IntersectionObserver(
      ([entry]) => {
        const isHalfVisible =
          entry.isIntersecting &&
          entry.intersectionRatio >= REQUIRED_VISIBILITY_RATIO;

        isHalfVisibleRef.current = isHalfVisible;

        if (isHalfVisible) {
          startViewTimer();
        } else {
          clearViewTimer();
        }
      },
      {
        threshold: [0, REQUIRED_VISIBILITY_RATIO, 1],
      }
    );

    observer.observe(postElement);
    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    return () => {
      clearViewTimer();
      isHalfVisibleRef.current = false;

      observer.disconnect();

      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      );
    };
  }, [
    postId,
    postAuthorUsername,
    currentViewerUsername,
    isSponsored,
    enabled,
  ]);

  return postRef;
};

export default usePostView;
